import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export function TermsPage() {
  return (
    <>
      <Helmet>
        <title>Terms of Service — RegattaReplay™</title>
        <meta
          name="description"
          content="Terms of Service for RegattaReplay™ — the digital home of amateur sailboat racing."
        />
      </Helmet>
      <div className="max-w-[720px] mx-auto px-5 py-12">
        <p className="text-sm text-muted-foreground mb-1">Last Updated: 2026-06-25</p>
        <h1 className="text-3xl font-bold text-navy-900 mb-8">Terms of Service</h1>

        {/* Plain-Language Summary */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-10">
          <p className="text-sm font-medium text-navy-900 mb-3">
            Plain-Language Summary
          </p>
          <p className="text-sm text-muted-foreground mb-2 italic">
            This summary helps you understand the key points. The full terms below are
            legally binding.
          </p>
          <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
            <li>
              <strong>You own your content.</strong> Uploading to RegattaReplay™ doesn't
              transfer ownership.
            </li>
            <li>
              <strong>You give us permission to use it.</strong> We need the right to
              display your tracks, create replays, and share them — that's the whole
              point of the platform.
            </li>
            <li>
              <strong>Race replays are collaborative.</strong> When multiple sailors'
              tracks form a replay, we keep the assembled replay even if one participant
              leaves. We can't delete one person's data without breaking the replay for
              everyone.
            </li>
            <li>
              <strong>We may create aggregate products.</strong> Anonymized fleet
              analytics, race density maps, wind patterns — built from community data,
              owned by us.
            </li>
            <li>
              <strong>You can delete your data.</strong> Request removal anytime. We'll
              delete your personal data and raw tracks within 30 days.
            </li>
            <li>
              <strong>We're free to use.</strong> No subscription fees. We may have
              sponsors.
            </li>
            <li>
              <strong>GPS tracks are not official records.</strong> Don't use our replays
              for protest hearings.
            </li>
          </ul>
        </div>

        {/* Section 1 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            1. Agreement to Terms
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            By creating an account on RegattaReplay™ ("the Service"), you agree to these
            Terms of Service ("Terms") and our{' '}
            <Link to="/privacy" className="text-brand-ink hover:underline">
              Privacy Policy
            </Link>
            . If you do not agree, do not use the Service.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed">
            You must be at least 13 years old to use the Service. If you are under 18,
            you represent that your parent or guardian has reviewed and consented to these
            Terms.
          </p>
        </section>

        {/* Section 2 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">2. The Service</h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            RegattaReplay™ is a platform for the sailing community that provides:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1 mb-3">
            <li>
              <strong>Replay</strong>: GPS-based race replays, performance analytics,
              and sailing history
            </li>
            <li>
              <strong>Find</strong>: Regatta discovery, crew matching, and club
              information
            </li>
            <li>
              <strong>Learn</strong>: Racing rules, tactics, and educational content
            </li>
          </ul>
          <p className="text-sm text-slate-700 leading-relaxed">
            The Service is provided free of charge. We may display sponsor content on
            the platform.
          </p>
        </section>

        {/* Section 3 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">3. Your Account</h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            You are responsible for maintaining the security of your account and for all
            activity under your account. You agree to provide accurate information when
            creating your account.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed">
            We may suspend or terminate your account if you violate these Terms.
          </p>
        </section>

        {/* Section 4 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">4. Your Content</h2>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.1 Ownership
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            You retain all ownership rights in content you upload to RegattaReplay™,
            including GPS tracks, photos, videos, race data, and profile information
            ("Your Content"). Nothing in these Terms transfers ownership of Your Content
            to us.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.2 License Grant to RegattaReplay™
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            By uploading or submitting Your Content to the Service, you grant
            RegattaReplay™ a{' '}
            <strong>
              non-exclusive, worldwide, royalty-free, sublicensable, and transferable
              license
            </strong>{' '}
            to use, reproduce, modify, adapt, display, distribute, and create derivative
            works from Your Content in connection with the Service and RegattaReplay™'s
            operations, including promoting and improving the Service.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed mb-2">
            This license includes the right to:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            <li>Process and display your GPS tracks as race replays</li>
            <li>
              Include your data in multi-participant race replays alongside other sailors
            </li>
            <li>Generate performance analytics from your track data</li>
            <li>Display your race history and statistics</li>
            <li>Use your content in promotional materials for the Service</li>
            <li>Create thumbnails, previews, and excerpts of your content</li>
          </ul>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.3 License Grant to Other Users
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            You also grant other users of the Service a non-exclusive license to view
            and interact with Your Content as made available through the Service (for
            example, watching a race replay that includes your track).
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.4 Multi-Participant Replays
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            Race replays combine GPS tracks from multiple participants into a single,
            collaborative product.{' '}
            <strong>
              Because other sailors depend on the assembled replay, the license you
              grant for multi-participant replays continues even after you delete your
              account or remove your raw track data.
            </strong>{' '}
            We will anonymize your personal identifiers in the replay upon deletion, but
            the track data within the assembled replay will be retained to preserve the
            experience for other participants.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.5 Aggregate and Anonymized Data
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            We may create{' '}
            <strong>anonymized, aggregated products</strong> from community data,
            including fleet movement analytics, race density maps, wind corridor
            analysis, and performance benchmarks. These aggregate products are owned by
            Tario Marine Technologies. They do not identify individual sailors and are
            not subject to deletion requests.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.6 License Duration
          </h3>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            <li>
              <strong>Raw GPS tracks, photos, videos, and profile data</strong>: The
              license ends when you delete the content or close your account, subject to
              the multi-participant replay exception in Section 4.4.
            </li>
            <li>
              <strong>Multi-participant replays</strong>: License is perpetual for the
              assembled replay (your personal identifiers will be anonymized upon
              deletion).
            </li>
            <li>
              <strong>Aggregate/anonymized data products</strong>: Owned by Tario
              Marine Technologies in perpetuity.
            </li>
          </ul>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.7 Your Representations
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed mb-2">
            You represent and warrant that:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            <li>
              You own Your Content or have the right to grant the licenses described
              above
            </li>
            <li>
              Your Content does not infringe any third party's intellectual property or
              privacy rights
            </li>
            <li>Your Content does not contain malicious code</li>
            <li>
              You participated in any race for which you upload GPS track data (or have
              permission from the participant)
            </li>
          </ul>
        </section>

        {/* Section 5 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            5. Race Results and Public Data
          </h2>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            5.1 Publicly Available Race Results
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            RegattaReplay™ collects and displays publicly available race results,
            including event names, dates, venues, boat names, skipper and crew names,
            sail numbers, finishing positions, and elapsed times. These are facts about
            events that occurred in public and are collected for the purpose of
            maintaining a comprehensive sailing records archive.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            5.2 Claiming Your Profile
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            When we display race results that include your name, you may claim your
            profile by verifying your identity through the Service. Claiming your profile
            gives you control over your display preferences and lets you link your race
            history.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            5.3 Requesting Removal
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            If you want your personal information removed from publicly collected race
            results, contact us at{' '}
            <a
              href="mailto:privacy@regattazone.com"
              className="text-brand-ink hover:underline"
            >
              privacy@regattazone.com
            </a>
            . We will process removal requests within 30 days. See our{' '}
            <Link to="/privacy" className="text-brand-ink hover:underline">
              Privacy Policy
            </Link>{' '}
            for details.
          </p>
        </section>

        {/* Section 6 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            6. Acceptable Use
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-2">
            You agree not to:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            <li>
              Upload GPS tracks from events you did not participate in (without the
              participant's permission)
            </li>
            <li>Create fake profiles or impersonate other sailors</li>
            <li>
              Use the crew matching features to harass, spam, or contact users for
              purposes unrelated to sailing
            </li>
            <li>
              Attempt to access non-public areas of the Service or circumvent security
              measures
            </li>
            <li>
              Use automated tools to access the Service at a rate that impairs it for
              other users
            </li>
            <li>
              Upload content that is illegal, defamatory, or violates others' rights
            </li>
          </ul>
        </section>

        {/* Section 7 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            7. Club and Race Organizer Accounts
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            If you use the Service on behalf of a sailing club, race committee, or
            organizing authority ("Organization"), you represent that you have authority
            to bind the Organization to these Terms. Content uploaded on behalf of an
            Organization is subject to the same license terms. The Organization retains
            ownership but grants RegattaReplay™ the same license described in Section
            4.2.
          </p>
        </section>

        {/* Section 8 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            8. Intellectual Property
          </h2>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            8.1 Our Content
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            The Service, including its design, features, software, text, graphics, and
            logos, is owned by Tario Marine Technologies and protected by copyright and
            other intellectual property laws. You may not copy, modify, or distribute
            our content without permission.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            8.2 Trademarks
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            "RegattaReplay™" and associated logos are trademarks owned by Tario Marine
            Technologies. You may not use our trademarks without prior written
            permission.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            8.3 Feedback
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            If you provide feedback, suggestions, or ideas about the Service, you grant
            us a perpetual, irrevocable, royalty-free license to use that feedback
            without restriction or compensation.
          </p>
        </section>

        {/* Section 9 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            9. GPS Data Disclaimer
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            <strong>
              GPS tracks are for informational and entertainment purposes only.
            </strong>{' '}
            They are not official race records. GPS accuracy varies based on device
            quality, satellite coverage, environmental conditions, and other factors.
            RegattaReplay™ makes no representation that GPS tracks accurately reflect
            actual boat positions or race outcomes.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed font-medium">
            Do not use RegattaReplay™ data for protest hearings, official scoring,
            safety-of-life decisions, or navigation. Official race results are determined
            solely by the race committee of each event.
          </p>
        </section>

        {/* Section 10 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">10. Privacy</h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            Your use of the Service is also governed by our{' '}
            <Link to="/privacy" className="text-brand-ink hover:underline">
              Privacy Policy
            </Link>
            , which describes how we collect, use, and share your personal information.
            By using the Service, you consent to our data practices as described in the
            Privacy Policy.
          </p>
        </section>

        {/* Section 11 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            11. Third-Party Services
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            The Service may integrate with third-party platforms (YachtScoring,
            ClubSpot, Sailwave, etc.) to display race results. We are not responsible
            for the accuracy of data from third-party sources, and we do not control or
            endorse those services.
          </p>
        </section>

        {/* Section 12 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            12. Disclaimers
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed uppercase mb-3">
            The Service is provided "as is" and "as available" without warranties of any
            kind, express or implied, including warranties of merchantability, fitness
            for a particular purpose, and non-infringement.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed mb-2">
            We do not warrant that:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            <li>The Service will be uninterrupted or error-free</li>
            <li>GPS data will be accurate or complete</li>
            <li>Race results will be correct</li>
            <li>The Service will meet your specific requirements</li>
          </ul>
        </section>

        {/* Section 13 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            13. Limitation of Liability
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed uppercase">
            To the maximum extent permitted by law, Tario Marine Technologies shall not
            be liable for any indirect, incidental, special, consequential, or punitive
            damages arising from your use of the Service. Our total liability shall not
            exceed the amount you paid us in the 12 months preceding the claim (which,
            for a free service, is zero).
          </p>
        </section>

        {/* Section 14 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            14. Indemnification
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            You agree to indemnify and hold harmless Tario Marine Technologies from any
            claims, damages, or expenses arising from your violation of these Terms or
            your use of the Service.
          </p>
        </section>

        {/* Section 15 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            15. Changes to Terms
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            We may update these Terms from time to time. We will notify you of material
            changes by email at least 30 days before they take effect. Your continued use
            of the Service after changes take effect constitutes acceptance. If you
            disagree with changes, you may close your account.
          </p>
        </section>

        {/* Section 16 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            16. Termination
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-2">
            You may close your account at any time. Upon account closure:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1 mb-3">
            <li>Your personal profile data will be deleted</li>
            <li>Your raw GPS tracks will be deleted from our storage</li>
            <li>
              Multi-participant replays containing your data will be retained with your
              identifiers anonymized (per Section 4.4)
            </li>
            <li>Aggregate data products will be retained (per Section 4.5)</li>
          </ul>
          <p className="text-sm text-slate-700 leading-relaxed">
            We may terminate your account for violation of these Terms with reasonable
            notice.
          </p>
        </section>

        {/* Section 17 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            17. Governing Law
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            These Terms are governed by the laws of the Province of Ontario and the
            federal laws of Canada applicable therein, without regard to conflict of law
            principles. Any disputes arising from these Terms shall be resolved in the
            courts of Ontario, Canada.
          </p>
        </section>

        {/* Section 18 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">18. General</h2>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            <li>
              These Terms constitute the entire agreement between you and Tario Marine
              Technologies regarding the Service.
            </li>
            <li>
              If any provision is found unenforceable, the remaining provisions remain in
              effect.
            </li>
            <li>
              Our failure to enforce any provision does not waive our right to enforce it
              later.
            </li>
            <li>
              You may not assign your rights under these Terms without our consent. We
              may assign our rights freely.
            </li>
          </ul>
        </section>

        {/* Section 19 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">19. Contact</h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-2">
            For questions about these Terms, contact us at:
          </p>
          <ul className="text-sm text-slate-700 list-none space-y-1">
            <li>
              <strong>Email</strong>:{' '}
              <a
                href="mailto:privacy@regattazone.com"
                className="text-brand-ink hover:underline"
              >
                privacy@regattazone.com
              </a>
            </li>
            <li>
              <strong>Website</strong>:{' '}
              <a
                href="https://regattazone.com"
                className="text-brand-ink hover:underline"
              >
                regattazone.com
              </a>
            </li>
          </ul>
        </section>
      </div>
    </>
  );
}
